import { FormControl, FormGroup, NgForm, Validators } from '@angular/forms';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  OnInit,
  viewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  TimeLogAllowedPeriod,
  TimeLogEntry,
  TimeLogEntryMode,
  TimeLogLoader,
} from '../../models/time-log';
import { TimeLogController } from '../../services/time-log.controller';
import { TimeLogService } from '../../services/time-log.service';
import moment from 'moment';
import { HolidaysService } from '../../services';
import { IHoliday } from '../../models';
import { lastValueFrom } from 'rxjs';
import { getDateRange } from '../../utils';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';

@Component({
  selector: 'app-time-log-input',
  templateUrl: './time-log-input.component.html',
  styleUrls: ['./time-log-input.component.scss'],
  providers: [{ provide: MAT_DATE_LOCALE, useValue: 'ja-JP' }],
  standalone: false,
})
export class TimeLogInputComponent implements OnInit, AfterViewChecked {
  translate = inject(TranslateService);
  private ctrl = inject(TimeLogController);
  private timeLogService = inject(TimeLogService);
  private holidayService = inject(HolidaysService);
  private _adapter = inject<DateAdapter<any>>(DateAdapter);
  private _locale = inject(MAT_DATE_LOCALE);
  private changeDetection = inject(ChangeDetectorRef);

  readonly mode = input<TimeLogEntryMode>('add');
  readonly projectId = input<number>(undefined);
  readonly employeeId = input<number>(undefined);
  readonly isContractor = input<boolean>(false);
  readonly entry = input<TimeLogEntry>(undefined);
  isLoading = true;
  maxHours = 8;
  selectedDays = [];
  form: FormGroup;
  allHolidays: Array<IHoliday> = [];
  readonly timeLogForm = viewChild<NgForm>('timeLogForm');
  maxLoggedDay: string;
  allowedPeriod: TimeLogAllowedPeriod;

  constructor() {
    this.isWorkingDay = this.isWorkingDay.bind(this);
    this.isLoggableDay = this.isLoggableDay.bind(this);
    this.isLoggableEndDay = this.isLoggableEndDay.bind(this);
  }

  async ngOnInit() {
    this.allHolidays = await lastValueFrom(this.holidayService.getUserAllHolidays());
    this.form = new FormGroup({
      description: new FormControl(this.entry()?.description || '', Validators.required),
      hours: new FormControl<number | null>(this.entry()?.hours || null, Validators.required),
      startDate: new FormControl<Date>(this.entry()?.startDate || new Date(), Validators.required),
      endDate: new FormControl<Date>(this.entry()?.endDate || new Date(), Validators.required),
    });
    if (this.mode() === 'add') {
      this.allowedPeriod = await this.timeLogService.getAllowedPeriod();
    }
    this.isLoading = false;
    this.handleDatePick();
    this._locale = 'en-GB';
    this._adapter.setLocale(this._locale);
    this.maxLoggedDay = this.setMaxLoggedDay(this.form.controls.startDate.value);
  }

  ngAfterViewChecked() {
    this.changeDetection.detectChanges();
  }

  private setMaxLoggedDay = (startDate: string) =>
    moment(startDate).endOf('month').format('YYYY-MM-DD');

  handleDatePick(fromDate = false) {
    const startDate = this.form.controls.startDate.value;

    if (fromDate) {
      this.form.controls.endDate.setValue(startDate);
    }

    this.selectedDays = getDateRange(startDate, this.form.controls.endDate.value).filter(
      (dateStr: string) => this.isWorkingDay(moment(dateStr, 'YYYY-MM-DD').toDate()),
    );

    this.maxLoggedDay = this.setMaxLoggedDay(startDate);
  }

  async submit() {
    const dates = this.selectedDays.map((item) => ({
      date: item,
      description: this.form.controls.description.value,
      hours: this.form.controls.hours.value,
    }));

    this.form.disable();

    let success: boolean | Error;

    if (this.mode() === 'add') {
      success = await this.ctrl.addEntry({
        employeeId: this.employeeId(),
        projectId: this.projectId(),
        dates,
      });
    } else {
      success = await this.ctrl.updateEntry(this.entry()!.id, {
        description: this.form.controls.description.value,
        hours: this.form.controls.hours.value,
      });
    }

    this.form.enable();
    if (typeof success === 'boolean') {
      this.timeLogForm().resetForm({
        description: '',
        hours: null,
        date: new Date(),
      });
    }
  }

  get isWorking() {
    if (this.mode() === 'add') {
      return this.ctrl.isLoading(TimeLogLoader.add);
    } else {
      this.form.controls.startDate.disable();
      this.form.controls.endDate.disable();
      return this.ctrl.isLoading(TimeLogLoader.edit);
    }
  }

  public isLoggableDay(date: Date | null): boolean {
    const day = moment(date).format('YYYY-MM-DD');

    return (
      this.isWorkingDay(date) && day >= this.allowedPeriod.from && day <= this.allowedPeriod.to
    );
  }

  public isLoggableEndDay(date: Date | null): boolean {
    const day = moment(date).format('YYYY-MM-DD');
    const startDay = moment(this.form.controls.startDate.value).format('YYYY-MM-DD');

    return this.isLoggableDay(date) && day >= startDay && day <= this.maxLoggedDay;
  }

  public isWorkingDay(date: Date | null): boolean {
    if (!date) {
      return false;
    }

    const day = date.getDay();
    const isWeekend: boolean = day === 0 || day === 6;

    if (this.isContractor()) {
      return !isWeekend;
    }

    const isHoliday = this.allHolidays.some(
      (h) => new Date(h.date).toLocaleDateString() === date.toLocaleDateString(),
    );

    return !isWeekend && !isHoliday;
  }

  public getErrorMessage(inputName: string): string {
    switch (inputName) {
      case 'description': {
        if (this.form.controls.description.hasError('required'))
          return this.translate.instant('LOG_TIME.INPUT.DESCRIPTION_REQUIRED');
        break;
      }

      case 'hours': {
        if (this.form.controls.hours.hasError('required'))
          return this.translate.instant('LOG_TIME.INPUT.HOURS_REQUIRED');
        if (this.form.controls.hours.hasError('min'))
          return this.translate.instant('LOG_TIME.INPUT.HOURS_REQUIRED');
        if (this.form.controls.hours.hasError('max'))
          return this.translate.instant('LOG_TIME.INPUT.HOURS_MAX');
        break;
      }

      case 'startDate': {
        if (this.form.controls.description.hasError('required'))
          return this.translate.instant('LOG_TIME.INPUT.START_DATE_REQUIRED');
        break;
      }

      case 'endDate': {
        if (this.form.controls.description.hasError('required'))
          return this.translate.instant('LOG_TIME.INPUT.END_DATE_REQUIRED');
        break;
      }
    }
  }

  getLoggedDateClass = (date: Date) => {
    const formattedDate = (d) => moment(d).format('YYYY-MM-DD');

    return (
      this.ctrl.data.value.entries.find(
        (entryDate) => formattedDate(date) === formattedDate(entryDate.date),
      ) && 'logged-date'
    );
  };
}
